/* eslint-disable react-hooks/set-state-in-effect */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  Copy,
  Filter,
  Loader2,
  Search,
  SlidersHorizontal,
  Star,
} from 'lucide-react';
import { colorEngine, type DeltaEMethod, type NamedColorMatch, type NamedColorSource } from '../lib/colorEngine';
import {
  findNearestPantone,
  PantoneServiceError,
  type PantoneSwatch,
  searchPantoneSwatches,
} from '../lib/pantoneService';
import type { PantoneAuthState } from '../lib/pantoneAuth';
import { useToolState } from '../contexts/ToolStateContext';
import { useColorActions } from '../hooks/useColorActions';
import { Tooltip } from './Tooltip';

interface NearestColorSearchPanelProps {
  baseHex: string;
  pantoneState: PantoneAuthState;
  copiedField: string | null;
  onCopy: (value: string, field: string) => void;
}

interface AnnotatedPantoneMatch extends PantoneSwatch {
  deltaE: number | null;
}

const DELTA_METHOD_OPTIONS: Array<{ id: DeltaEMethod; label: string; description: string }> = [
  {
    id: 'lab',
    label: 'ΔE2000 (Lab)',
    description: 'Industry-standard perceptual difference based on CIE L*a*b*.',
  },
  {
    id: 'oklab',
    label: 'Euclidean (OKLab)',
    description: 'Perceptual distance in Björn Ottosson\'s OKLab space.',
  },
];

const PUBLIC_DATASET_OPTIONS: Array<{ id: NamedColorSource | 'all'; label: string; description: string }> = [
  { id: 'all', label: 'CSS + XKCD', description: 'Search both CSS named colors and the XKCD color survey dataset.' },
  { id: 'css', label: 'CSS only', description: 'Search only the 148 standard CSS named colors.' },
  { id: 'xkcd', label: 'XKCD only', description: 'Search only the ~950 XKCD color survey names.' },
];

const DELTA_THRESHOLD_MIN = 0;
const DELTA_THRESHOLD_MAX = 20;

const formatDelta = (delta: number | null) => (delta == null ? '—' : delta.toFixed(3));

const passesThreshold = (delta: number | null, threshold: number) => {
  if (!Number.isFinite(threshold) || threshold >= DELTA_THRESHOLD_MAX) {
    return true;
  }
  if (delta == null) {
    return true;
  }
  return delta <= threshold;
};

export function NearestColorSearchPanel({ baseHex, pantoneState, copiedField, onCopy }: NearestColorSearchPanelProps) {
  const normalizedBase = baseHex?.trim() ?? '';
  const hasBaseColor = normalizedBase.length > 0;
  const canUsePantone = pantoneState.status === 'authenticated' && !pantoneState.offline;

  const { getToolState, setToolState } = useToolState();
  const persisted = getToolState('nearest');

  const [deltaMethod, setDeltaMethod] = useState<DeltaEMethod>((persisted?.deltaMethod as DeltaEMethod) ?? 'lab');
  const [deltaThreshold, setDeltaThreshold] = useState<number>(persisted?.deltaThreshold ?? 5);
  const [datasetFilter, setDatasetFilter] = useState<NamedColorSource | 'all'>((persisted?.datasetFilter as NamedColorSource | 'all') ?? 'all');
  const [expandedMatchId, setExpandedMatchId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(persisted?.searchQuery ?? '');

  useEffect(() => {
    setToolState('nearest', { deltaMethod, deltaThreshold, datasetFilter, searchQuery });
  }, [deltaMethod, deltaThreshold, datasetFilter, searchQuery, setToolState]);

  const { setActiveColor, saveToFavorites } = useColorActions();

  const nearestAbortControllerRef = useRef<AbortController | null>(null);
  const searchAbortControllerRef = useRef<AbortController | null>(null);

  const [nearestRaw, setNearestRaw] = useState<PantoneSwatch[]>([]);
  const [nearestLoading, setNearestLoading] = useState(false);
  const [nearestError, setNearestError] = useState<string | null>(null);
  const [nearestFetched, setNearestFetched] = useState(false);
  const [nearestNotice, setNearestNotice] = useState<string | undefined>(undefined);

  const [searchRaw, setSearchRaw] = useState<PantoneSwatch[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchFetched, setSearchFetched] = useState(false);
  const [searchNotice, setSearchNotice] = useState<string | undefined>(undefined);

  useEffect(() => {
    return () => {
      nearestAbortControllerRef.current?.abort();
      searchAbortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    nearestAbortControllerRef.current?.abort();

    if (!hasBaseColor) {
      setNearestRaw([]);
      setNearestLoading(false);
      setNearestError(null);
      setNearestFetched(false);
      setNearestNotice(undefined);
      return;
    }

    if (!canUsePantone) {
      setNearestRaw([]);
      setNearestLoading(false);
      setNearestError(
        pantoneState.offline
          ? 'Pantone Connect is unavailable offline. Reconnect to fetch licensed swatches.'
          : 'Authenticate with Pantone Connect to view licensed swatches.',
      );
      setNearestFetched(false);
      setNearestNotice(undefined);
      return;
    }

    const controller = new AbortController();
    nearestAbortControllerRef.current = controller;

    setNearestLoading(true);
    setNearestError(null);
    setNearestFetched(false);

    findNearestPantone(normalizedBase, { signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        setNearestRaw(response.matches);
        setNearestNotice(response.licensingNotice);
        setNearestLoading(false);
        setNearestFetched(true);
        setNearestError(response.matches.length ? null : 'No Pantone matches were returned for this color.');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        const message = error instanceof PantoneServiceError ? error.message : 'Failed to fetch Pantone matches.';
        setNearestRaw([]);
        setNearestNotice(undefined);
        setNearestLoading(false);
        setNearestFetched(true);
        setNearestError(message);
      });

    return () => controller.abort();
  }, [canUsePantone, hasBaseColor, normalizedBase, pantoneState.offline]);

  useEffect(() => {
    setExpandedMatchId(null);
  }, [normalizedBase, deltaMethod]);

  const annotatePantoneMatches = useCallback(
    (matches: PantoneSwatch[]): AnnotatedPantoneMatch[] => {
      if (!hasBaseColor) {
        return [];
      }
      return matches.map((match) => ({
        ...match,
        deltaE: colorEngine.calculateDeltaE(normalizedBase, match.hex, deltaMethod),
      }));
    },
    [deltaMethod, hasBaseColor, normalizedBase],
  );

  const nearestMatches = useMemo(() => annotatePantoneMatches(nearestRaw), [annotatePantoneMatches, nearestRaw]);
  const searchMatches = useMemo(() => annotatePantoneMatches(searchRaw), [annotatePantoneMatches, searchRaw]);

  const filteredNearestMatches = useMemo(
    () => nearestMatches.filter((match) => passesThreshold(match.deltaE, deltaThreshold)),
    [deltaThreshold, nearestMatches],
  );

  const filteredSearchMatches = useMemo(
    () => searchMatches.filter((match) => passesThreshold(match.deltaE, deltaThreshold)),
    [deltaThreshold, searchMatches],
  );

  const fallbackMatches = useMemo(() => {
    if (canUsePantone || !hasBaseColor) {
      return [] as NamedColorMatch[];
    }
    const sources = datasetFilter === 'all' ? undefined : [datasetFilter];
    return colorEngine.nearestNamedColors(normalizedBase, { sources, limit: 6, method: deltaMethod }).filter((match) =>
      passesThreshold(match.deltaE, deltaThreshold),
    );
  }, [canUsePantone, datasetFilter, deltaMethod, deltaThreshold, hasBaseColor, normalizedBase]);

  const handleSearch = useCallback(() => {
    searchAbortControllerRef.current?.abort();

    if (!searchQuery.trim()) {
      setSearchRaw([]);
      setSearchLoading(false);
      setSearchFetched(false);
      setSearchError('Enter a Pantone identifier to search.');
      setSearchNotice(undefined);
      return;
    }

    if (!canUsePantone) {
      setSearchRaw([]);
      setSearchLoading(false);
      setSearchFetched(false);
      setSearchNotice(undefined);
      setSearchError(
        pantoneState.offline
          ? 'Pantone Connect is unavailable offline. Reconnect to search licensed swatches.'
          : 'Authenticate with Pantone Connect to search licensed swatches.',
      );
      return;
    }

    const controller = new AbortController();
    searchAbortControllerRef.current = controller;

    setSearchLoading(true);
    setSearchError(null);
    setSearchFetched(false);

    searchPantoneSwatches(searchQuery.trim(), controller.signal)
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        setSearchRaw(response.matches);
        setSearchNotice(response.licensingNotice);
        setSearchLoading(false);
        setSearchFetched(true);
        setSearchError(response.matches.length ? null : 'No Pantone swatches matched this query.');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        const message = error instanceof PantoneServiceError ? error.message : 'Pantone search failed.';
        setSearchRaw([]);
        setSearchNotice(undefined);
        setSearchLoading(false);
        setSearchFetched(true);
        setSearchError(message);
      });
  }, [canUsePantone, pantoneState.offline, searchQuery]);

  const activeNotice = canUsePantone ? nearestNotice ?? searchNotice : undefined;

  const renderActionBar = useCallback(
    (hex: string, fieldId: string) => {
      const isCopied = copiedField === fieldId;
      return (
        <div className="nearest-card__actions">
          <button
            type="button"
            className="nearest-card__action-btn"
            title="Use as active color"
            aria-label="Use as active color"
            onClick={(e) => { e.stopPropagation(); setActiveColor({ hex }); }}
          >
            <ArrowUpRight className="nearest-card__action-icon" />
          </button>
          <button
            type="button"
            className="nearest-card__action-btn"
            title="Save to favorites"
            aria-label="Save to favorites"
            onClick={(e) => { e.stopPropagation(); saveToFavorites(hex); }}
          >
            <Star className="nearest-card__action-icon" />
          </button>
          <button
            type="button"
            className={`nearest-card__copy-btn${isCopied ? ' nearest-card__copy-btn--copied' : ''}`}
            onClick={(e) => { e.stopPropagation(); onCopy(hex, fieldId); }}
          >
            <Copy className="nearest-card__copy-icon" />
            <span aria-live="polite">{isCopied ? 'Copied \u2713' : 'Copy HEX'}</span>
          </button>
        </div>
      );
    },
    [copiedField, onCopy, saveToFavorites, setActiveColor],
  );

  const renderPantoneMatch = useCallback(
    (match: AnnotatedPantoneMatch) => {
      const fieldId = `pantone-${match.id}`;
      const isExpanded = expandedMatchId === match.id;
      return (
        <div key={match.id} className="nearest-card nearest-card--expandable">
          <button
            type="button"
            className="nearest-card__toggle"
            aria-expanded={isExpanded}
            onClick={() => {
              setExpandedMatchId(isExpanded ? null : match.id);
            }}
          >
            <div className="nearest-card__row">
              <div className="nearest-card__swatch" style={{ backgroundColor: match.hex }} />
              <div>
                <div className="nearest-card__name">{match.displayName}</div>
                <div className="nearest-card__source">{match.id}</div>
              </div>
            </div>
            <div className="nearest-card__row">
              <span className="nearest-card__delta">{formatDelta(match.deltaE)}</span>
              {isExpanded ? <ChevronUp className="nearest-card__action-icon" /> : <ChevronDown className="nearest-card__action-icon" />}
            </div>
          </button>
          {isExpanded && (
            <div className="nearest-card__expand-detail">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 'var(--s-4)' }}>
                <div>
                  <div className="nearest-card__source" style={{ marginBottom: 'var(--s-1)' }}>HEX</div>
                  <div className="nearest-card__meta-value">{match.hex}</div>
                </div>
                {match.lab && (
                  <div>
                    <div className="nearest-card__source" style={{ marginBottom: 'var(--s-1)' }}>CIELAB</div>
                    <div className="nearest-card__meta-value">L {match.lab.l.toFixed(1)}, a {match.lab.a.toFixed(1)}, b {match.lab.b.toFixed(1)}</div>
                  </div>
                )}
                {match.cmyk && (
                  <div>
                    <div className="nearest-card__source" style={{ marginBottom: 'var(--s-1)' }}>CMYK</div>
                    <div className="nearest-card__meta-value">{match.cmyk.c}/{match.cmyk.m}/{match.cmyk.y}/{match.cmyk.k}</div>
                  </div>
                )}
              </div>
              {renderActionBar(match.hex, fieldId)}
            </div>
          )}
        </div>
      );
    },
    [expandedMatchId, renderActionBar],
  );

  const renderFallbackMatch = useCallback(
    (match: NamedColorMatch) => {
      const fieldId = `fallback-${match.id}`;
      return (
        <div key={match.id} className="nearest-card">
          <div className="nearest-card__row">
            <div className="nearest-card__swatch" style={{ backgroundColor: match.hex }} />
            <div>
              <div className="nearest-card__name">{match.displayName}</div>
              <div className="nearest-card__source">{match.source.toUpperCase()}</div>
            </div>
          </div>
          <div className="nearest-card__meta">
            <span>HEX: <span className="nearest-card__meta-value">{match.hex}</span></span>
            <span>ΔE: {formatDelta(match.deltaE)}</span>
          </div>
          {renderActionBar(match.hex, fieldId)}
        </div>
      );
    },
    [renderActionBar],
  );

  return (
    <section className="nearest-card" style={{ padding: 'var(--s-10) 28px', gap: 'var(--s-8)' }}>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--t-h3)', fontWeight: 'var(--w-semi)', letterSpacing: '-0.01em', color: 'var(--ink)' }}>
          Nearest Color Search
        </h3>
        <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-2)' }}>
          Explore nearest Pantone swatches or public named colors for the current selection. Adjust ΔE metric and threshold to refine results.
        </p>
      </header>

      <div className="nearest-card" style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: 'var(--panel)',
        padding: 'var(--s-3) 28px',
        margin: '0 -28px',
        borderBottom: '1px solid var(--line)',
        gap: 'var(--s-4)',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--s-4)' }}>
          <div>
            <h4 style={{ margin: '0 0 var(--s-2)', fontSize: 'var(--t-eyebrow)', fontWeight: 'var(--w-semi)', textTransform: 'uppercase', color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
              <Filter style={{ width: 14, height: 14 }} />
              ΔE metric
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)' }}>
              {DELTA_METHOD_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setDeltaMethod(option.id)}
                  title={option.description}
                  className={`status-pill${deltaMethod === option.id ? ' status-pill--neutral' : ''}`}
                  style={deltaMethod !== option.id ? { background: 'var(--panel)', border: '1px solid var(--line)', color: 'var(--ink-2)' } : undefined}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <details style={{ display: 'none' }}>
            <summary style={{ fontSize: 'var(--t-eyebrow)', fontWeight: 'var(--w-semi)', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 'var(--s-2)', display: 'flex', alignItems: 'center', gap: 'var(--s-2)', cursor: 'pointer' }}>
              <SlidersHorizontal style={{ width: 14, height: 14 }} />
              ΔE threshold ({deltaThreshold})
            </summary>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-4)' }}>
              <input
                type="range"
                min={DELTA_THRESHOLD_MIN}
                max={DELTA_THRESHOLD_MAX}
                value={deltaThreshold}
                onChange={(event) => setDeltaThreshold(Number(event.target.value))}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 'var(--t-body)', fontFamily: 'var(--font-mono)', color: 'var(--ink-2)', width: 32, textAlign: 'right' }}>{deltaThreshold}</span>
            </div>
          </details>
          <div>
            <h4 style={{ margin: '0 0 var(--s-2)', fontSize: 'var(--t-eyebrow)', fontWeight: 'var(--w-semi)', textTransform: 'uppercase', color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
              <SlidersHorizontal style={{ width: 14, height: 14 }} />
              ΔE threshold
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-4)' }}>
              <input
                type="range"
                min={DELTA_THRESHOLD_MIN}
                max={DELTA_THRESHOLD_MAX}
                value={deltaThreshold}
                onChange={(event) => setDeltaThreshold(Number(event.target.value))}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 'var(--t-body)', fontFamily: 'var(--font-mono)', color: 'var(--ink-2)', width: 32, textAlign: 'right' }}>{deltaThreshold}</span>
            </div>
            <p style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--t-caption)', color: 'var(--ink-3)' }}>
              Limit matches to colors within the selected ΔE. Set to {DELTA_THRESHOLD_MAX}+ to show all results.
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-6)' }}>
        {canUsePantone && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
            <div className="nearest-section__header">
              <h4 className="nearest-section__title">Nearest Pantone matches</h4>
            </div>
            {nearestLoading && (
              <p style={{ fontSize: 'var(--t-body)', color: 'var(--ink-2)', display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
                <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                Fetching Pantone matches…
              </p>
            )}
            {nearestError && <p style={{ fontSize: 'var(--t-body)', color: 'var(--bad)' }}>{nearestError}</p>}
            {!nearestLoading && filteredNearestMatches.length === 0 && nearestFetched && !nearestError && (
              <p style={{ fontSize: 'var(--t-body)', color: 'var(--ink-2)' }}>No Pantone matches within the selected ΔE threshold.</p>
            )}
            {filteredNearestMatches.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--s-4)' }}>
                {filteredNearestMatches.map((match) => renderPantoneMatch(match))}
              </div>
            )}
          </div>
        )}

        {canUsePantone && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
            <div className="nearest-section__header">
              <h4 className="nearest-section__title">Search Pantone ID</h4>
            </div>
            <div style={{ display: 'flex', gap: 'var(--s-3)' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="e.g. 186 C, PANTONE 3005 U"
                  className="context-bar__input"
                  style={{ width: '100%', paddingRight: 36 }}
                />
                <Search style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--ink-3)' }} />
              </div>
              <button
                type="button"
                onClick={handleSearch}
                disabled={!searchQuery.trim() || searchLoading}
                className="status-pill status-pill--neutral"
                style={{ cursor: searchLoading || !searchQuery.trim() ? 'not-allowed' : 'pointer', opacity: searchLoading || !searchQuery.trim() ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 'var(--s-2)', padding: '8px 14px', fontWeight: 'var(--w-semi)' }}
              >
                {searchLoading ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Search style={{ width: 14, height: 14 }} />}
                Search
              </button>
            </div>
            {searchError && <p style={{ fontSize: 'var(--t-body)', color: 'var(--bad)' }}>{searchError}</p>}
            {!searchLoading && filteredSearchMatches.length === 0 && searchFetched && !searchError && (
              <p style={{ fontSize: 'var(--t-body)', color: 'var(--ink-2)' }}>No Pantone swatches matched this query within the selected ΔE threshold.</p>
            )}
            {filteredSearchMatches.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--s-4)' }}>
                {filteredSearchMatches.map((match) => renderPantoneMatch(match))}
              </div>
            )}
          </div>
        )}
      </div>

      {!canUsePantone && hasBaseColor && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-6)' }}>
          <div>
            <div className="nearest-section__header">
              <h4 className="nearest-section__title">Nearest public named colors</h4>
              <span className="nearest-section__note">Public datasets only — Pantone requires authentication</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
              {PUBLIC_DATASET_OPTIONS.map((option) => (
                <Tooltip key={option.id} content={option.description} position="bottom">
                  <button
                    type="button"
                    onClick={() => setDatasetFilter(option.id)}
                    className={`status-pill${datasetFilter === option.id ? ' status-pill--neutral' : ''}`}
                    style={datasetFilter !== option.id ? { background: 'var(--panel)', border: '1px solid var(--line)', color: 'var(--ink-2)' } : undefined}
                  >
                    {option.label}
                  </button>
                </Tooltip>
              ))}
            </div>
          </div>
          {fallbackMatches.length === 0 ? (
            <p style={{ fontSize: 'var(--t-body)', color: 'var(--ink-2)' }}>No named colors met the current ΔE threshold.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--s-4)' }}>
              {fallbackMatches.map((match) => renderFallbackMatch(match))}
            </div>
          )}
        </div>
      )}

      {activeNotice && (
        <p style={{ fontSize: 'var(--t-caption)', color: 'var(--ink-3)', borderTop: '1px solid var(--line)', paddingTop: 'var(--s-4)', margin: 0 }}>
          {activeNotice}
        </p>
      )}
    </section>
  );
}
